import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ListTodo, Bot, Play, Users, Settings, Trash2, BarChart3 } from 'lucide-react';
import { useProject, useUpdateProject, useDeleteProject } from '../hooks/useProjects';
import { useTasks } from '../hooks/useTasks';
import { useAgents } from '../hooks/useAgents';
import { useInstances } from '../hooks/useInstances';
import { useTeams } from '../hooks/useTeams';
import TaskBoard from '../components/TaskBoard';
import TeamCard from '../components/TeamCard';
import AgentGallery from '../components/AgentGallery';
import ProjectMcpServers from '../components/ProjectMcpServers';
import ProjectSkills from '../components/ProjectSkills';

type Tab = 'tasks' | 'agents' | 'instances' | 'teams' | 'analytics' | 'settings';

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: project, isLoading } = useProject(id);
  const { data: tasks } = useTasks(id);
  const { data: agents } = useAgents(id);
  const { data: instances } = useInstances(id);
  const { data: teams } = useTeams(id);
  const updateProject = useUpdateProject(id ?? '');
  const deleteProject = useDeleteProject();
  const [tab, setTab] = useState<Tab>('tasks');
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editing, setEditing] = useState(false);

  if (isLoading) {
    return (
      <div className="p-8 text-sm text-slate-400">Loading project...</div>
    );
  }

  if (!project) {
    return (
      <div className="p-8 text-sm text-slate-500">Project not found.</div>
    );
  }

  const handleStartEdit = () => {
    setEditName(project.name);
    setEditDesc(project.description ?? '');
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    await updateProject.mutateAsync({ name: editName, description: editDesc });
    setEditing(false);
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this project? This cannot be undone.')) return;
    await deleteProject.mutateAsync(project.id);
    navigate('/projects');
  };

  const tabs: { key: Tab; label: string; icon: typeof ListTodo }[] = [
    { key: 'tasks', label: 'Tasks', icon: ListTodo },
    { key: 'agents', label: 'Agents', icon: Bot },
    { key: 'instances', label: 'Instances', icon: Play },
    { key: 'teams', label: 'Teams', icon: Users },
    { key: 'analytics', label: 'Analytics', icon: BarChart3 },
    { key: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="p-8">
      <button
        onClick={() => navigate('/projects')}
        className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to projects
      </button>

      <h1 className="mb-1 text-2xl font-bold text-slate-900">{project.name}</h1>
      {project.description && (
        <p className="mb-6 text-sm text-slate-500">{project.description}</p>
      )}

      <div className="mb-6 flex gap-1 border-b border-slate-200">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
              tab === key
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'tasks' && (
        <TaskBoard tasks={tasks ?? []} agents={agents} instances={instances} projectId={id!} />
      )}

      {tab === 'agents' && (
        <AgentGallery projectId={id!} agents={agents} instances={instances} />
      )}

      {tab === 'instances' && (
        <div>
          {!instances?.length ? (
            <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center">
              <Play className="mx-auto mb-3 h-10 w-10 text-slate-300" />
              <p className="text-sm text-slate-500">No instances yet.</p>
              <button
                onClick={() => navigate(`/projects/${id}/instances`)}
                className="mt-3 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
              >
                Go to Instances
              </button>
            </div>
          ) : (
            <div>
              <div className="mb-4 flex justify-end">
                <button
                  onClick={() => navigate(`/projects/${id}/instances`)}
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                >
                  Open Instances Panel
                </button>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {instances.map((inst) => (
                  <div
                    key={inst.id}
                    onClick={() => navigate(`/instances/${inst.id}`)}
                    className="cursor-pointer rounded-xl border border-slate-200 bg-white p-5 hover:border-slate-300"
                  >
                    <div className="mb-2 flex items-center gap-2">
                      <div className={`h-2 w-2 rounded-full ${
                        inst.status === 'running' ? 'bg-green-500' :
                        inst.status === 'paused' ? 'bg-yellow-500' :
                        inst.status === 'failed' ? 'bg-red-500' :
                        'bg-slate-400'
                      }`} />
                      <span className="text-xs font-medium text-slate-500 capitalize">{inst.status}</span>
                    </div>
                    <h3 className="font-medium text-slate-900">{inst.agent?.name ?? 'Unknown'}</h3>
                    <p className="mt-1 truncate text-xs text-slate-500">
                      {inst.task?.title ?? 'Custom prompt'}
                    </p>
                    <div className="mt-2 flex gap-3 text-xs text-slate-400">
                      <span>Turn {inst.currentTurn}/{inst.maxTurns}</span>
                      <span>${Number(inst.totalCostUsd).toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'teams' && (
        <div>
          {!teams?.length ? (
            <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center">
              <Users className="mx-auto mb-3 h-10 w-10 text-slate-300" />
              <p className="text-sm text-slate-500">No teams yet. Teams will appear here when created via the API or MCP.</p>
            </div>
          ) : (
            <div>
              <div className="mb-4 flex justify-end">
                <button
                  onClick={() => navigate(`/projects/${id}/teams`)}
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                >
                  Open Teams Panel
                </button>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {teams.map((team) => (
                  <TeamCard key={team.id} team={team} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'analytics' && (
        <div className="text-center">
          <BarChart3 className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="mb-3 text-sm text-slate-500">View detailed cost and usage analytics for this project.</p>
          <button
            onClick={() => navigate(`/projects/${id}/analytics`)}
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Open Analytics
          </button>
        </div>
      )}

      {tab === 'settings' && (
        <div className="max-w-lg space-y-6">
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <ProjectMcpServers projectId={id!} />
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <ProjectSkills projectId={id!} />
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <h3 className="mb-4 font-semibold text-slate-900">Project details</h3>
            {editing ? (
              <div className="space-y-4">
                <div>
                  <label htmlFor="edit-name" className="mb-1.5 block text-sm font-medium text-slate-700">Name</label>
                  <input
                    id="edit-name"
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                  />
                </div>
                <div>
                  <label htmlFor="edit-desc" className="mb-1.5 block text-sm font-medium text-slate-700">Description</label>
                  <textarea
                    id="edit-desc"
                    rows={3}
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleSaveEdit}
                    disabled={updateProject.isPending}
                    className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                  >
                    {updateProject.isPending ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setEditing(false)}
                    className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-sm text-slate-600"><span className="font-medium">Name:</span> {project.name}</p>
                <p className="mt-2 text-sm text-slate-600"><span className="font-medium">Description:</span> {project.description || 'None'}</p>
                <button
                  onClick={handleStartEdit}
                  className="mt-4 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Edit
                </button>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-red-200 bg-white p-6">
            <h3 className="mb-2 font-semibold text-red-600">Danger zone</h3>
            <p className="mb-4 text-sm text-slate-500">
              Deleting this project will remove all tasks and agents associated with it.
            </p>
            <button
              onClick={handleDelete}
              disabled={deleteProject.isPending}
              className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
              {deleteProject.isPending ? 'Deleting...' : 'Delete Project'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
